import { IEmployee } from "../common/data/dtos/employee";
import { IGetEmployeeByEmail } from "../common/data/dtos/get-employee-by-email";
import { IWorkOrderCreator } from "../common/data/dtos/work-order-creator";
import { DELETE_TYPED_ASYNC, GET_TYPED_ASYNC, POST_TYPED_ASYNC, PUT_TYPED_ASYNC } from "../helpers/httpHelper";

const BASE_PATH = 'api/Employee';

interface EmployeeResponse {
    success: boolean;
    employee?: IEmployee;
    employees?: IEmployee[];
    error?: string;
}

interface GetEmployeeByEmailResponse {
    success: boolean;
    employee?: IGetEmployeeByEmail;
    error?: string;
}

interface GetWorkOrderCreatorResponse {
    success: boolean;
    employees?: IWorkOrderCreator[];
    error?: string;
}

type CreateEmployeeType = Omit<IEmployee, 'id'> | Partial<IEmployee>;

const createEmployee = async (employee: CreateEmployeeType): Promise<EmployeeResponse> => {
    try {
        const response = await POST_TYPED_ASYNC<IEmployee>(`${BASE_PATH}`, employee);
        return {
            success: response.success,
            ...(!response.success ? { error: response.message || response.exception } : null),
        };
    } catch (err: any) {
        return { success: false, error: err.message };
    }
};

const deleteEmployee = async (id: string): Promise<EmployeeResponse> => {
    try {
        const response = await DELETE_TYPED_ASYNC<IEmployee>(`${BASE_PATH}/${id}`);

        return {
            success: response.success,
            ...(!response.success ? { error: response.message || response.exception } : null),
        };
    } catch (err: any) {
        return { success: false, error: err.message };
    }
};

const getEmployees = async (): Promise<EmployeeResponse> => {
    try {
        const response = await GET_TYPED_ASYNC<IEmployee>(`${BASE_PATH}`);

        if (!response.success) {
            return { success: response.success, error: response.message || response.exception };
        }

        return { success: response.success, employees: response.collection };
    } catch (err: any) {
        return { success: false, error: err.message };
    }
};

const getOneEmployee = async (id: string): Promise<EmployeeResponse> => {
    try {
        const response = await GET_TYPED_ASYNC<IEmployee>(`${BASE_PATH}?id=${id}`);

        if (!response.success) {
            return { success: response.success, error: response.message || response.exception };
        }

        return { success: response.success, employee: response.entity };
    } catch (err: any) {
        return { success: false, error: err.message };
    }
};

const getByEmail = async (email: string): Promise<GetEmployeeByEmailResponse> => {
    try {
        const response = await GET_TYPED_ASYNC<IGetEmployeeByEmail>(`${BASE_PATH}/GetByEmail?email=${email}`);

        if (!response.success) {
            return { success: response.success, error: response.message || response.exception };
        }

        return { success: response.success, employee: response.entity };
    } catch (err: any) {
        return { success: false, error: err.message };
    }
};

const updateEmployee = async (employee: IEmployee): Promise<EmployeeResponse> => {
    try {
        const response = await PUT_TYPED_ASYNC<IEmployee>(`${BASE_PATH}/${employee.id}`, employee);
        return {
            success: response.success,
            ...(!response.success ? { error: response.message || response.exception } : null),
        };
    } catch (err: any) {
        return { success: false, error: err.message };
    }
};

const getByWorkOrderCreator = async (): Promise<GetWorkOrderCreatorResponse> => {
    try {
        const response = await GET_TYPED_ASYNC<IWorkOrderCreator>(`${BASE_PATH}/GetByWorkOrderCreator`);

        if (!response.success) {
            return { success: response.success, error: response.message || response.exception };
        }

        return { success: response.success, employees: response.collection };
    } catch (err: any) {
        return { success: false, error: err.message };
    }
};

export default {
    createEmployee,
    deleteEmployee,
    getEmployees,
    getOneEmployee,
    updateEmployee,
    getByEmail,
    getByWorkOrderCreator,
};
